// Predictive Mode, from the page's side: whether to ask the athlete, their answer, and a breakthrough waiting to be applied.
import { getRequest } from '@/Util/utils';

export type PredictiveState = {
  username: string;
  choice: 'on' | 'off' | null;
  applies: boolean;
  enabled: boolean;
  // Ended in this plan: it does not start again before the next plan.
  ended?: boolean;
  ask: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pending: any | null;
  previous_decay_method: number | null;
};

/** The name of a decay method as Profile Settings lists it; an account that never chose one has the default, Optimal. */
export function decayName(value: number | null | undefined): string {
  const v = Number(value);
  if (!v || v < 1) return 'Optimal';
  if (v === 1) return 'None';
  if (v <= 1.05) return 'Small';
  if (v <= 1.15) return 'Optimal';
  return 'Aggressive';
}

// The state endpoints always speak for the signed-in user. On a page that shows another athlete (a coach or admin looking
// at them) that state is the coach's own, so the page says whose page it is and the state is withheld when it is not theirs.
let pageUser: string | null = null;
let ownState: Promise<PredictiveState | null> | null = null;

export function setPredictivePageUser(username: string | null | undefined) {
  pageUser = username || null;
  ownState = null;
}

/** The state the signed-in user has, one request shared by everything on the page; fresh after an answer. */
async function readState(fresh: boolean): Promise<PredictiveState | null> {
  if (!ownState || fresh) {
    ownState = window
      .fetch('/predictive-mode/state', getRequest())
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }
  return ownState;
}

/** The Predictive Mode state of the athlete whose page this is; null when it is someone else's page or the state could not be read. */
export async function getPredictiveState(fresh = false): Promise<PredictiveState | null> {
  const state = await readState(fresh);
  return state && pageUser && state.username !== pageUser ? null : state;
}

/** The username of the athlete being viewed when that is not the signed-in user (a coach's view), else null. */
export async function viewedAthlete(): Promise<string | null> {
  const state = await readState(false);
  return state && pageUser && state.username !== pageUser ? pageUser : null;
}

/** An answer was given (a choice, a proposal applied or put aside): the next read asks the server again. */
export function forgetPredictiveState() {
  ownState = null;
}

export async function setPredictiveChoice(choice: 'on' | 'off'): Promise<boolean> {
  const response = await window.fetch('/predictive-mode/choice', { method: 'POST', ...getRequest(), body: JSON.stringify({ choice }) });
  forgetPredictiveState();
  return response.ok;
}

/**
 * Calls $callback with the state read again whenever the server finishes recalculating the athlete's history, which is when a
 * breakthrough has been processed. The proposal is made by the job that follows, so it is read once more shortly after.
 * Returns a function that stops watching.
 */
export function watchPredictiveState(callback: (state: PredictiveState | null) => void): () => void {
  // @ts-ignore the live connection of the page, absent without one
  const channel = window.Xert?._userChannel;
  if (!channel) return () => {};
  let later: ReturnType<typeof setTimeout> | null = null;
  const read = async () => callback(await getPredictiveState(true));
  const onComplete = () => {
    read();
    if (later) clearTimeout(later);
    later = setTimeout(read, 15000);
  };
  channel.bind('trickle_complete', onComplete);
  return () => {
    channel.unbind('trickle_complete', onComplete);
    if (later) clearTimeout(later);
  };
}
